/**
 * @typedef {Object} CollectionBarElements
 * @property {HTMLElement | null} collectionBar
 * @property {HTMLElement | null} collectionBarDetails
 * @property {HTMLElement | null} collectionBarDetailsText
 * @property {HTMLElement | null} collectionBarTopPosition
 * @property {NodeListOf<HTMLElement>} collectionBarLinks
 */

// TODO: account for main header stickiness when calculating the top position of the collection bar for the observer when unsticking the collection bar

class CollectionHeader extends HTMLElement {
  constructor() {
    super();

    /** @type {CollectionBarElements} */
    this.elements = {
      collectionBar: this.querySelector('.collection-bar'),
      collectionBarDetails: this.querySelector('.collection-bar__details'),
      collectionBarDetailsText: this.querySelector('.collection-bar__details span'),
      collectionBarTopPosition: this.querySelector('#collectionBarTopPosition'),
      collectionBarLinks: this.querySelectorAll('.collection-bar__link'),
    }

    this.observer = new IntersectionObserver(
      throttle(([entry]) => {
        this.classList.toggle('is-sticky', !entry.isIntersecting)
      }, 100),
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.0,
      }
    );

    this.handleLinkClick = this.handleLinkClick.bind(this);
  }

  /**
   * Toggles the loading state of a collection bar link.
   * @param {HTMLElement | Element} element - The collection bar link element
   * @param {boolean} state - The loading state
   */
  toggleLinkLoading(element, state) {
    element.classList.toggle('is-loading', state)
  }

  connectedCallback() {
    window.addEventListener('load', this.setDetailsTextWidth.bind(this))

    // Check if scroll position is beneath the collection bar
    if (window.scrollY > this.elements.collectionBarTopPosition.offsetTop)
      this.classList.add('is-sticky');

    this.observer.observe(this.elements.collectionBarTopPosition);

    this.elements.collectionBarLinks.forEach((link) => {
      link.addEventListener('click', this.handleLinkClick)
    })
  }

  disconnectedCallback() {
    this.observer.unobserve(this.elements.collectionBarTopPosition);
    this.elements.collectionBarLinks.forEach((link) => {
      link.removeEventListener('click', this.handleLinkClick)
    })
    window.removeEventListener('load', this.setDetailsTextWidth.bind(this));
  }

  setDetailsTextWidth() {
    this.collectionBarDetailsTextWidth = this.elements.collectionBarDetailsText.offsetWidth;
    this.style.setProperty('--collectionBarDetailsTextWidth', `${this.collectionBarDetailsTextWidth}px`);
  }

  /**
   * Handles click events on collection bar links by adding loading states and smooth scrolling
   * before navigating to the target URL.
   *
   * @param {Event} event - The click event object
   * @returns {void}
   */
  handleLinkClick(event) {
    event.preventDefault();
    /** @type {HTMLElement | null} */
    const item = event.target.closest('.collection-bar__item')
    const href = item ? item.querySelector('a').getAttribute('href') : null;
    if (!href) return;

    this.querySelectorAll('.collection-bar__item').forEach((item) => {
      this.toggleLinkLoading(item, false)
    })

    this.toggleLinkLoading(item, true)

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
    setTimeout(() => {
      window.location.href = href
    }, 100)
  }
}

customElements.define('collection-header', CollectionHeader)
